import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { 
  useGetPaymentsQuery, 
  useGetBillingAnalyticsQuery,
  useGetSubscriptionsQuery,
  useGetAccountsQuery,
  useGetSubscriptionMetricsQuery
} from '../store/api/billingApi'
import {
  useGetEnterpriseInvoicesQuery,
  useMarkEnterpriseInvoicePaidMutation,
  useCreateEnterpriseInvoiceMutation,
  useSearchUsersQuery,
  useGetUserSeatInfoQuery,
  useGetUserByIdQuery,
} from '../store/api/usersApi'
import StatsCard from '../components/StatsCard'
import { formatDateTimeEST } from '../utils/dateUtils'

// Import status constants
const TASK_STATUS = {
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled'
};

const USER_STATUS = {
  ACTIVE: 'active',
  PENDING: 'pending'
};

interface PaymentFilters {
  page: number;
  limit: number;
  status: string;
  paymentType: 'seat' | 'subscription' | 'credit' | 'collaborator_seat' | 'execution_seat' | 'other' | '';
  sortBy: string;
  sortOrder: 'ASC' | 'DESC';
}

const Billing: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterUserIdParam = searchParams.get('userId');
  const filterUserIdParsed = filterUserIdParam ? parseInt(filterUserIdParam, 10) : NaN;
  const filterUserId = Number.isFinite(filterUserIdParsed) ? filterUserIdParsed : null;
  const filterOrgIdParam = searchParams.get('orgId');
  const filterOrgIdParsed = filterOrgIdParam ? parseInt(filterOrgIdParam, 10) : NaN;
  const filterOrgId = Number.isFinite(filterOrgIdParsed) ? filterOrgIdParsed : null;

  const [filters, setFilters] = useState<PaymentFilters>({
    page: 1,
    limit: 20,
    status: '',
    paymentType: '',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });

  const [activeTab, setActiveTab] = useState<'payments' | 'subscriptions' | 'accounts' | 'analytics' | 'enterprise'>('enterprise');

  // API calls
  const { data: paymentsData, isLoading: paymentsLoading } = useGetPaymentsQuery(filters);
  const { data: billingAnalytics } = useGetBillingAnalyticsQuery();
  const { data: subscriptions, isLoading: subscriptionsLoading } = useGetSubscriptionsQuery();
  const { data: accountsData, isLoading: accountsLoading } = useGetAccountsQuery({ page: 1, limit: 20 });
  const { data: subscriptionMetrics } = useGetSubscriptionMetricsQuery();
  const { data: enterpriseInvoices, isLoading: enterpriseLoading, refetch: refetchEnterpriseInvoices } = useGetEnterpriseInvoicesQuery(
    filterUserId || undefined
  );
  const [markInvoicePaid] = useMarkEnterpriseInvoicePaidMutation();
  const [createInvoice] = useCreateEnterpriseInvoiceMutation();
  const { data: filterUser } = useGetUserByIdQuery(filterUserId as number, { skip: !filterUserId });
  const { data: filterSeatInfo } = useGetUserSeatInfoQuery(filterUserId as number, { skip: !filterUserId });

  const filteredEnterpriseInvoices = React.useMemo(() => {
    if (!enterpriseInvoices) return enterpriseInvoices;
    if (!filterUserId) return enterpriseInvoices;
    return enterpriseInvoices.filter((inv) => inv.userId === filterUserId || inv.accountId === filterSeatInfo?.accountId);
  }, [enterpriseInvoices, filterUserId, filterSeatInfo?.accountId]);

  const filteredPayments = React.useMemo(() => {
    if (!paymentsData?.data || !filterUser) return paymentsData;
    const email = (filterUser.email || '').toLowerCase();
    const name = (filterUser.name || '').toLowerCase();
    const data = paymentsData.data.filter((p: any) => {
      const pe = (p.userEmail || '').toLowerCase();
      const pn = (p.userName || '').toLowerCase();
      return (email && pe === email) || (name && pn && pn.includes(name));
    });
    return { ...paymentsData, data, meta: { ...paymentsData.meta, total: data.length } };
  }, [paymentsData, filterUser]);

  const filteredSubscriptions = React.useMemo(() => {
    if (!subscriptions || !filterUser) return subscriptions;
    const email = (filterUser.email || '').toLowerCase();
    return subscriptions.filter((s: any) => (s.userEmail || '').toLowerCase() === email);
  }, [subscriptions, filterUser]);

  const clearAccountFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('userId');
    next.delete('orgId');
    setSearchParams(next);
  };

  const setOrgFilter = (orgId: string) => {
    const next = new URLSearchParams(searchParams);
    if (orgId) next.set('orgId', orgId);
    else next.delete('orgId');
    setSearchParams(next);
  };

  // Create invoice modal state
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const [newInvoiceUserId, setNewInvoiceUserId] = useState('');
  const [newInvoiceSeats, setNewInvoiceSeats] = useState('');
  const [newInvoiceDescription, setNewInvoiceDescription] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [billingType, setBillingType] = useState<'PER_SEAT' | 'CUSTOM_TOTAL'>('PER_SEAT');
  const [customAmount, setCustomAmount] = useState('');
  const [periodMonths, setPeriodMonths] = useState('');
  
  // Search users for invoice creation
  const { data: searchedUsers } = useSearchUsersQuery(userSearchQuery, { skip: userSearchQuery.length < 2 });

  const displayPayments = filteredPayments;
  const displaySubscriptions = filteredSubscriptions || [];
  const displayEnterpriseInvoices = filteredEnterpriseInvoices;

  // Handle filter changes
  const handleFilterChange = (key: keyof PaymentFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString?: string) => formatDateTimeEST(dateString);

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case TASK_STATUS.COMPLETED:
      case USER_STATUS.ACTIVE:
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case USER_STATUS.PENDING:
        return 'bg-yellow-100 text-yellow-800';
      case TASK_STATUS.FAILED:
        return 'bg-red-100 text-red-800';
      case TASK_STATUS.CANCELLED:
        return 'bg-gray-100 text-gray-800';
      case 'refunded':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Show loading only for initial load
  const isInitialLoading = paymentsLoading && !paymentsData;

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
    <div>
      <h1 className="text-2xl font-bold text-gray-900">Billing & Payments</h1>
      <p className="text-gray-600 mt-2">Monitor payments, subscriptions, and revenue</p>
      </div>

      {filterUserId && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 flex flex-wrap items-center gap-3 justify-between">
          <div className="text-sm text-indigo-900">
            <span className="font-semibold">Account filter:</span>{' '}
            {filterUser?.name || `User #${filterUserId}`}
            {filterUser?.email ? ` (${filterUser.email})` : ''}
            {filterSeatInfo?.accountId ? ` · Account #${filterSeatInfo.accountId}` : ''}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {filterSeatInfo?.organizations && filterSeatInfo.organizations.length > 0 && (
              <select
                value={filterOrgId ?? ''}
                onChange={(e) => setOrgFilter(e.target.value)}
                className="text-sm border border-indigo-300 rounded-md px-2 py-1 bg-white"
              >
                <option value="">All organizations</option>
                {filterSeatInfo.organizations.map((org) => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={clearAccountFilter}
              className="text-sm px-3 py-1 rounded-md bg-white border border-indigo-300 text-indigo-800 hover:bg-indigo-100"
            >
              Clear filter
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Revenue"
          value={formatCurrency(billingAnalytics?.totalRevenue || 0)}
          subtitle="All-time revenue"
          trend={`${(billingAnalytics?.revenueGrowth ?? 0).toFixed(1)}%`}
          trendUp={(billingAnalytics?.revenueGrowth ?? 0) >= 0}
        />
        <StatsCard
          title="Monthly Revenue"
          value={formatCurrency(billingAnalytics?.monthlyRevenue || 0)}
          subtitle="This month"
          trend={`${(billingAnalytics?.revenueGrowth ?? 0).toFixed(1)}%`}
          trendUp={(billingAnalytics?.revenueGrowth ?? 0) >= 0}
        />
        <StatsCard
          title="Active Subscriptions"
          value={subscriptionMetrics?.activeSubscriptions || 0}
          subtitle="Currently active"
          trend={`${subscriptionMetrics?.newSubscriptions || 0} new`}
          trendUp={(subscriptionMetrics?.newSubscriptions || 0) >= 0}
        />
        <StatsCard
          title="Conversion Rate"
          value={`${(billingAnalytics?.conversionRate || 0).toFixed(1)}%`}
          subtitle="Payment success rate"
          trend={`${(billingAnalytics?.successRate || 0).toFixed(1)}% success`}
          trendUp={(billingAnalytics?.conversionRate || 0) >= 0}
        />
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { id: 'payments', label: 'Payments', count: displayPayments?.meta?.total || displayPayments?.data?.length || 0 },
              { id: 'subscriptions', label: 'Subscriptions', count: displaySubscriptions?.length || 0 },
              { id: 'accounts', label: 'Accounts', count: accountsData?.meta?.total || 0 },
              { id: 'enterprise', label: 'Enterprise Invoices', count: displayEnterpriseInvoices?.filter(i => i.status === 'PENDING').length || 0 },
              { id: 'analytics', label: 'Analytics', count: null }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                {tab.count !== null && (
                  <span className="bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Payments Tab */}
          {activeTab === 'payments' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="flex gap-3 flex-wrap">
                  <select
                    value={filters.paymentType}
                    onChange={(e) => handleFilterChange('paymentType', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Types</option>
                    <option value="credit">Credits</option>
                    <option value="collaborator_seat">Collaborator Seats</option>
                    <option value="execution_seat">Execution Seats</option>
                    <option value="seat">Full Seats</option>
                    <option value="subscription">Subscriptions</option>
                    <option value="other">Other Payments</option>
                  </select>

                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All Status</option>
                    <option value={TASK_STATUS.COMPLETED}>Completed</option>
                    <option value={USER_STATUS.PENDING}>Pending</option>
                    <option value={TASK_STATUS.FAILED}>Failed</option>
                  </select>

                  <select
                    value={filters.sortBy}
                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="created_at">Date</option>
                    <option value="amount">Amount</option>
                    <option value="status">Status</option>
                  </select>

                  <select
                    value={filters.sortOrder}
                    onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="DESC">Newest First</option>
                    <option value="ASC">Oldest First</option>
                  </select>
                </div>
              </div>

              {/* Payments Table */}
              <div className="overflow-x-auto">
                {paymentsLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : !displayPayments?.data || displayPayments?.data.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">
                      <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 mb-1">No payments found</h3>
                    <p className="text-sm text-gray-500">No payments match your current filters.</p>
                  </div>
                ) : (
                  <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment ID
                      </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Method
                      </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Invoice
                          </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Country
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                        {displayPayments?.data.map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {payment.id.startsWith('inv_') || payment.id.startsWith('pi_') ? (
                                <span className="text-xs text-gray-500">{payment.id}</span>
                              ) : (
                                `#${payment.id.substring(0, 20)}${payment.id.length > 20 ? '...' : ''}`
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {payment.paymentType && (
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  payment.paymentType === 'credit'
                                    ? 'bg-amber-100 text-amber-800'
                                    : payment.paymentType === 'collaborator_seat'
                                    ? 'bg-teal-100 text-teal-800'
                                    : payment.paymentType === 'execution_seat'
                                    ? 'bg-indigo-100 text-indigo-800'
                                    : payment.paymentType === 'seat' 
                                    ? 'bg-blue-100 text-blue-800' 
                                    : payment.paymentType === 'subscription'
                                    ? 'bg-purple-100 text-purple-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {{
                                    credit: 'Credit',
                                    collaborator_seat: 'Collab Seat',
                                    execution_seat: 'Exec Seat',
                                    seat: 'Full Seat',
                                    subscription: 'Subscription',
                                    other: 'Other',
                                  }[payment.paymentType] || payment.paymentType}
                                </span>
                              )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {payment.userEmail || payment.userName || 'Unknown user'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(payment.amount, payment.currency)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {payment.method}
                        </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {payment.invoiceUrl ? (
                                <a
                                  href={payment.invoiceUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-800"
                                  title="View invoice in Stripe"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path d="M10.75 3a.75.75 0 00-1.5 0v6.19L6.53 6.47a.75.75 0 10-1.06 1.06l4 4a.75.75 0 001.06 0l4-4a.75.75 0 10-1.06-1.06L10.75 9.19V3z" />
                                    <path d="M4 12.25a.75.75 0 011.5 0v2A1.75 1.75 0 007.25 16h5.5A1.75 1.75 0 0014.5 14.25v-2a.75.75 0 011.5 0v2A3.25 3.25 0 0112.75 17.5h-5.5A3.25 3.25 0 014 14.25v-2z" />
                                  </svg>
                                  <span className="text-xs font-medium">Invoice</span>
                                </a>
                              ) : (
                                <span className="text-gray-400 text-sm">—</span>
                              )}
                            </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                            {payment.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {payment.paymentCountry || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(payment.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                    {/* Pagination */}
                    {displayPayments?.meta && displayPayments?.meta.totalPages > 1 && (
                      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div className="flex-1 flex justify-between sm:hidden">
                          <button
                            onClick={() => handleFilterChange('page', Math.max(1, filters.page - 1))}
                            disabled={filters.page === 1}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>
                          <button
                            onClick={() => handleFilterChange('page', Math.min(displayPayments?.meta.totalPages, filters.page + 1))}
                            disabled={filters.page >= displayPayments?.meta.totalPages}
                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                          <div>
                            <p className="text-sm text-gray-700">
                              Showing <span className="font-medium">{(filters.page - 1) * filters.limit + 1}</span> to{' '}
                              <span className="font-medium">
                                {Math.min(filters.page * filters.limit, displayPayments?.meta.total)}
                              </span>{' '}
                              of <span className="font-medium">{displayPayments?.meta.total}</span> results
                            </p>
                          </div>
                          <div>
                            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                              <button
                                onClick={() => handleFilterChange('page', Math.max(1, filters.page - 1))}
                                disabled={filters.page === 1}
                                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Previous
                              </button>
                              <button
                                onClick={() => handleFilterChange('page', Math.min(displayPayments?.meta.totalPages, filters.page + 1))}
                                disabled={filters.page >= displayPayments?.meta.totalPages}
                                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Next
                              </button>
                            </nav>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Subscriptions Tab */}
          {activeTab === 'subscriptions' && (
            <div className="space-y-4">
              {subscriptionsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : !displaySubscriptions || displaySubscriptions.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">No subscriptions found</h3>
                  <p className="text-sm text-gray-500">No active subscriptions in your Stripe account.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Subscription ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Plan
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Seats
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Start Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Next Renewal
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {displaySubscriptions.map((subscription) => (
                        <tr key={subscription.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            #{subscription.id.substring(0, 20)}...
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {subscription.userEmail || subscription.userName || 'Unknown user'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {subscription.planName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(subscription.price, subscription.currency)} / {subscription.interval}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {subscription.quantity}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(subscription.status)}`}>
                              {subscription.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(subscription.startDate)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(subscription.currentPeriodEnd)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Accounts Tab */}
          {activeTab === 'accounts' && (
            <div className="space-y-4">
              {accountsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : !accountsData?.data || accountsData.data.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">No accounts found</h3>
                  <p className="text-sm text-gray-500">No customer accounts in your Stripe account.</p>
                </div>
              ) : (
                <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Account
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Subscription
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Paid
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Country
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {accountsData.data.map((account) => (
                        <tr key={account.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {account.accountName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {account.userEmail || account.userName || 'Unknown user'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {account.subscriptionPlan ? (
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(account.subscriptionStatus || 'none')}`}>
                                {account.subscriptionPlan}
                              </span>
                            ) : (
                              <span className="text-gray-500">No subscription</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(account.totalPaid, account.currency || 'USD')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {account.country || '—'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(account.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                  {/* Pagination */}
                  {accountsData.meta && accountsData.meta.totalPages > 1 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                      <div className="flex-1 flex justify-between sm:hidden">
                        <button
                          onClick={() => handleFilterChange('page', Math.max(1, (accountsData.meta?.page || 1) - 1))}
                          disabled={(accountsData.meta?.page || 1) === 1}
                          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => handleFilterChange('page', Math.min(accountsData.meta.totalPages, (accountsData.meta?.page || 1) + 1))}
                          disabled={(accountsData.meta?.page || 1) >= accountsData.meta.totalPages}
                          className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm text-gray-700">
                            Showing <span className="font-medium">{((accountsData.meta?.page || 1) - 1) * (accountsData.meta?.limit || 20) + 1}</span> to{' '}
                            <span className="font-medium">
                              {Math.min((accountsData.meta?.page || 1) * (accountsData.meta?.limit || 20), accountsData.meta.total)}
                            </span>{' '}
                            of <span className="font-medium">{accountsData.meta.total}</span> results
                          </p>
                        </div>
                        <div>
                          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                            <button
                              onClick={() => handleFilterChange('page', Math.max(1, (accountsData.meta?.page || 1) - 1))}
                              disabled={(accountsData.meta?.page || 1) === 1}
                              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Previous
                            </button>
                            <button
                              onClick={() => handleFilterChange('page', Math.min(accountsData.meta.totalPages, (accountsData.meta?.page || 1) + 1))}
                              disabled={(accountsData.meta?.page || 1) >= accountsData.meta.totalPages}
                              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Next
                            </button>
                          </nav>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Enterprise Invoices Tab */}
          {activeTab === 'enterprise' && (
            <div className="space-y-4">
              {/* Create Invoice Button */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => setShowCreateInvoiceModal(true)}
                  className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create Invoice
                </button>
              </div>

              {enterpriseLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                </div>
              ) : !displayEnterpriseInvoices || displayEnterpriseInvoices.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 mb-4">
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">No enterprise invoices found</h3>
                  <p className="text-sm text-gray-500">Enterprise invoices will appear here when seats are modified for enterprise accounts. Click "Create Invoice" to manually create one.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Enterprise Billing:</strong> Enterprise accounts (subscription_id = 2) receive manual invoices. 
                      When you add seats for an enterprise user, an invoice is created here. Mark it as paid once payment is received.
                    </p>
                  </div>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Invoice ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Billing
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Seats
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Period
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {displayEnterpriseInvoices.map((invoice) => (
                        <tr key={invoice.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <div>ENT-{invoice.id.toString().padStart(5, '0')}</div>
                            <div className="text-xs text-gray-500">{invoice.description}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              invoice.billingType === 'CUSTOM_TOTAL' 
                                ? 'bg-purple-100 text-purple-800' 
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {invoice.billingType === 'CUSTOM_TOTAL' ? 'Custom' : 'Per Seat'}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div>{invoice.userName || 'Unknown'}</div>
                            <div className="text-xs text-gray-500">{invoice.userEmail}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {invoice.seatsAdded > 0 && (
                              <span className="text-green-600 font-medium">+{invoice.seatsAdded}</span>
                            )}
                            {invoice.seatsRemoved > 0 && (
                              <span className="text-red-600 font-medium">-{invoice.seatsRemoved}</span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {invoice.periodMonths ? (
                              <div>
                                <span className="font-medium">{invoice.periodMonths} mo</span>
                                {invoice.expiresAt && (
                                  <div className="text-xs text-gray-500">
                                    Expires: {formatDate(invoice.expiresAt)}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className={invoice.amount < 0 ? 'text-red-600 font-medium' : 'text-green-600 font-medium'}>
                              {formatCurrency(Math.abs(invoice.amount))}
                            </span>
                            {invoice.billingType === 'CUSTOM_TOTAL' && invoice.periodMonths && invoice.seatsAdded > 0 && (
                              <div className="text-xs text-gray-500">
                                ${(invoice.amount / invoice.seatsAdded / invoice.periodMonths).toFixed(2)}/seat/mo
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              invoice.status === 'PAID' 
                                ? 'bg-green-100 text-green-800' 
                                : invoice.status === 'PENDING'
                                ? 'bg-yellow-100 text-yellow-800'
                                : invoice.status === 'APPLIED'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {invoice.status}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(invoice.createdAt)}
                            {invoice.paidAt && (
                              <div className="text-xs text-green-600">
                                Paid: {formatDate(invoice.paidAt)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {invoice.status === 'PENDING' && invoice.amount > 0 && (
                              <button
                                onClick={async () => {
                                  if (window.confirm(`Mark invoice ENT-${invoice.id.toString().padStart(5, '0')} as paid?`)) {
                                    try {
                                      await markInvoicePaid(invoice.id).unwrap();
                                      refetchEnterpriseInvoices();
                                      alert('Invoice marked as paid successfully');
                                    } catch (err: any) {
                                      alert(err?.data?.message || 'Failed to mark invoice as paid');
                                    }
                                  }
                                }}
                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Mark Paid
                              </button>
                            )}
                            {invoice.status === 'PAID' && (
                              <span className="text-green-600 text-xs">✓ Paid</span>
                            )}
                            {invoice.status === 'APPLIED' && (
                              <span className="text-blue-600 text-xs">Credit Applied</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Create Invoice Modal */}
              {showCreateInvoiceModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-xl font-bold text-gray-900">Create Enterprise Invoice</h2>
                      <button 
                        onClick={() => {
                          setShowCreateInvoiceModal(false);
                          setNewInvoiceUserId('');
                          setNewInvoiceSeats('');
                          setNewInvoiceDescription('');
                          setUserSearchQuery('');
                          setBillingType('PER_SEAT');
                          setCustomAmount('');
                          setPeriodMonths('');
                        }} 
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <div className="space-y-4">
                      {/* User Search */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Search User (by name or email):
                        </label>
                        <input
                          type="text"
                          value={userSearchQuery}
                          onChange={(e) => setUserSearchQuery(e.target.value)}
                          placeholder="Type to search users..."
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                        {searchedUsers && searchedUsers.length > 0 && userSearchQuery.length >= 2 && (
                          <div className="mt-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                            {searchedUsers.map((user: any) => (
                              <button
                                key={user.id}
                                onClick={() => {
                                  setNewInvoiceUserId(String(user.id));
                                  setUserSearchQuery(`${user.name} (${user.email})`);
                                }}
                                className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm"
                              >
                                <div className="font-medium">{user.name}</div>
                                <div className="text-gray-500 text-xs">{user.email} (ID: {user.id})</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* User ID */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          User ID:
                        </label>
                        <input
                          type="number"
                          value={newInvoiceUserId}
                          onChange={(e) => setNewInvoiceUserId(e.target.value)}
                          placeholder="Enter user ID"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>

                      {/* Billing Type Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Billing Type:
                        </label>
                        <div className="flex gap-4">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              name="billingType"
                              value="PER_SEAT"
                              checked={billingType === 'PER_SEAT'}
                              onChange={() => setBillingType('PER_SEAT')}
                              className="mr-2"
                            />
                            <span className="text-sm">Per Seat ($39/seat)</span>
                          </label>
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              name="billingType"
                              value="CUSTOM_TOTAL"
                              checked={billingType === 'CUSTOM_TOTAL'}
                              onChange={() => setBillingType('CUSTOM_TOTAL')}
                              className="mr-2"
                            />
                            <span className="text-sm">Custom Total for Period</span>
                          </label>
                        </div>
                      </div>

                      {/* Number of Seats */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Number of Seats:
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={newInvoiceSeats}
                          onChange={(e) => setNewInvoiceSeats(e.target.value)}
                          placeholder="e.g., 30"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>

                      {/* Custom Total Fields - Only show when CUSTOM_TOTAL is selected */}
                      {billingType === 'CUSTOM_TOTAL' && (
                        <>
                          {/* Period in Months */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Period (Months):
                            </label>
                            <select
                              value={periodMonths}
                              onChange={(e) => setPeriodMonths(e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            >
                              <option value="">Select period...</option>
                              <option value="1">1 Month</option>
                              <option value="2">2 Months</option>
                              <option value="3">3 Months</option>
                              <option value="6">6 Months</option>
                              <option value="12">12 Months (1 Year)</option>
                              <option value="24">24 Months (2 Years)</option>
                            </select>
                          </div>

                          {/* Custom Amount */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Total Amount ($):
                            </label>
                            <input
                              type="number"
                              min="1"
                              step="0.01"
                              value={customAmount}
                              onChange={(e) => setCustomAmount(e.target.value)}
                              placeholder="e.g., 2000"
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>
                        </>
                      )}

                      {/* Invoice Summary */}
                      {newInvoiceSeats && parseInt(newInvoiceSeats) > 0 && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm font-medium text-blue-800 mb-1">Invoice Summary:</p>
                          <p className="text-sm text-blue-700">
                            {billingType === 'PER_SEAT' ? (
                              <>
                                {newInvoiceSeats} seats × $39/seat = <strong>${(parseInt(newInvoiceSeats) * 39).toFixed(2)}</strong>
                              </>
                            ) : (
                              <>
                                {newInvoiceSeats} seats for {periodMonths || '?'} month{periodMonths && parseInt(periodMonths) > 1 ? 's' : ''} = <strong>${customAmount || '?'}</strong>
                                {periodMonths && customAmount && (
                                  <span className="text-xs block mt-1">
                                    (${(parseFloat(customAmount) / parseInt(newInvoiceSeats) / parseInt(periodMonths)).toFixed(2)}/seat/month)
                                  </span>
                                )}
                              </>
                            )}
                          </p>
                        </div>
                      )}

                      {/* Description */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description (optional):
                        </label>
                        <input
                          type="text"
                          value={newInvoiceDescription}
                          onChange={(e) => setNewInvoiceDescription(e.target.value)}
                          placeholder="e.g., Enterprise plan activation"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>

                      {/* Actions */}
                      <div className="flex justify-end gap-2 pt-4">
                        <button
                          onClick={() => {
                            setShowCreateInvoiceModal(false);
                            setNewInvoiceUserId('');
                            setNewInvoiceSeats('');
                            setNewInvoiceDescription('');
                            setUserSearchQuery('');
                            setBillingType('PER_SEAT');
                            setCustomAmount('');
                            setPeriodMonths('');
                          }}
                          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={async () => {
                            if (!newInvoiceUserId || !newInvoiceSeats) {
                              alert('Please enter User ID and Number of Seats');
                              return;
                            }
                            if (billingType === 'CUSTOM_TOTAL' && (!customAmount || !periodMonths)) {
                              alert('Please enter Period and Total Amount for custom billing');
                              return;
                            }
                            try {
                              const result = await createInvoice({
                                userId: parseInt(newInvoiceUserId),
                                seats: parseInt(newInvoiceSeats),
                                description: newInvoiceDescription || undefined,
                                billingType,
                                customAmount: billingType === 'CUSTOM_TOTAL' ? parseFloat(customAmount) : undefined,
                                periodMonths: billingType === 'CUSTOM_TOTAL' ? parseInt(periodMonths) : undefined
                              }).unwrap();
                              
                              const periodText = billingType === 'CUSTOM_TOTAL' && periodMonths 
                                ? ` for ${periodMonths} month${parseInt(periodMonths) > 1 ? 's' : ''}`
                                : '';
                              const expiresText = result.expiresAt 
                                ? `\nExpires: ${formatDate(result.expiresAt)}`
                                : '';
                              
                              alert(`✅ Invoice created successfully!\nInvoice ID: ${result.invoiceId}\nAmount: $${result.amount}${periodText}${expiresText}`);
                              setShowCreateInvoiceModal(false);
                              setNewInvoiceUserId('');
                              setNewInvoiceSeats('');
                              setNewInvoiceDescription('');
                              setUserSearchQuery('');
                              setBillingType('PER_SEAT');
                              setCustomAmount('');
                              setPeriodMonths('');
                              refetchEnterpriseInvoices();
                            } catch (err: any) {
                              alert(`❌ Failed to create invoice: ${err?.data?.message || err?.message || 'Unknown error'}`);
                            }
                          }}
                          disabled={!newInvoiceUserId || !newInvoiceSeats || (billingType === 'CUSTOM_TOTAL' && (!customAmount || !periodMonths))}
                          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Create Invoice
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              {/* Revenue Analytics */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue Analytics</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary-600">
                      {formatCurrency(billingAnalytics?.averageOrderValue || 0)}
                    </div>
                    <div className="text-sm text-gray-500">Average Order Value</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {(billingAnalytics?.revenueGrowth || 0).toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-500">Revenue Growth</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {formatCurrency(subscriptionMetrics?.monthlyRecurringRevenue || 0)}
                    </div>
                    <div className="text-sm text-gray-500">Monthly Recurring Revenue</div>
                  </div>
                </div>
              </div>

              {/* Subscription Metrics */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Subscription Metrics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="text-center">
                    <div className="text-xl font-bold text-blue-600">
                      {subscriptionMetrics?.totalSubscriptions || 0}
                    </div>
                    <div className="text-sm text-gray-500">Total Subscriptions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-green-600">
                      {subscriptionMetrics?.newSubscriptions || 0}
                    </div>
                    <div className="text-sm text-gray-500">New This Month</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-red-600">
                      {subscriptionMetrics?.churned || 0}
                    </div>
                    <div className="text-sm text-gray-500">Churned</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-purple-600">
                      {(subscriptionMetrics?.churnRate || 0).toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-500">Churn Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-indigo-600">
                      {subscriptionMetrics?.totalSeats || 0}
                    </div>
                    <div className="text-sm text-gray-500">Total Seats</div>
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              {billingAnalytics?.paymentMethodDistribution && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Methods</h3>
                  <div className="space-y-2">
                    {billingAnalytics.paymentMethodDistribution.map((method, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">{method.method}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-primary-600 h-2 rounded-full"
                              style={{ width: `${method.percentage}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-500 w-8">{method.percentage}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Countries */}
              {billingAnalytics?.topPayingCountries && billingAnalytics.topPayingCountries.length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Top Paying Countries</h3>
                  <div className="space-y-2">
                    {billingAnalytics.topPayingCountries.map((country) => (
                      <div key={country.country} className="flex justify-between items-center text-sm text-gray-700">
                        <span>{country.country}</span>
                        <div className="flex items-center gap-3">
                          <span>{formatCurrency(country.revenue)}</span>
                          <span className="text-gray-500">{country.percentage}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Billing; 